import { type RefObject, useCallback, useMemo } from 'react';
import {
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import gridKeyboardCoordinates from './gridKeyboardCoordinates';
import type { GalleryItem } from './ImageGallery.common';
import { galleryItemKey } from './ImageGallery.common';

/**
 * The part of a drop dnd-kit gives both the drag handler and the announcer,
 * which reports a narrower event than the handler receives.
 */
export type ReorderDrop = {
  active: { id: unknown };
  over?: { id: unknown } | null;
};

type UseGridReorderArgs = {
  images: GalleryItem[];
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isReorderable?: (index: number) => boolean;
  gridRef?: RefObject<HTMLElement | null>;
  /** Holds the grid still while a reorder is saving, rather than dropping the next one. */
  isReordering?: boolean;
};

type UseGridReorderReturn = {
  sensors: ReturnType<typeof useSensors>;
  sortableKeys: string[];
  /** Whether a tile takes part in the sort at all, saving or not. */
  isSortable: (index: number) => boolean;
  /** Index of the item a dnd-kit id belongs to, or -1. */
  indexOfKey: (key: unknown) => number;
  /** Whether a drop would actually move the photo. */
  willReorder: (event: ReorderDrop) => boolean;
  onDragEnd: (event: DragEndEvent) => void;
};

const DRAG_ACTIVATION_DISTANCE = 5;
const TOUCH_DRAG_ACTIVATION_DELAY = 200;
const TOUCH_DRAG_TOLERANCE = 5;

const useGridReorder = ({
  images,
  onReorder,
  isReorderable,
  gridRef,
  isReordering = false,
}: UseGridReorderArgs): UseGridReorderReturn => {
  const getColumns = useCallback((): number => {
    const grid = gridRef?.current;

    if (!grid) {
      return 1;
    }

    return getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
  }, [gridRef]);

  const coordinateGetter = useMemo(() => gridKeyboardCoordinates(getColumns), [getColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_DRAG_ACTIVATION_DELAY,
        tolerance: TOUCH_DRAG_TOLERANCE,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter })
  );

  // Saving does not change which tiles sort: unmounting them mid-drop would
  // strip dnd-kit of the node it animates into place and of the handle that
  // keyboard focus returns to. `SortableImageTile` is disabled instead.
  const isSortable = useCallback(
    (index: number): boolean =>
      index >= 0 && Boolean(onReorder) && (isReorderable?.(index) ?? true),
    [onReorder, isReorderable]
  );

  const sortableKeys = useMemo(
    () => images.filter((_, index) => isSortable(index)).map(galleryItemKey),
    [images, isSortable]
  );

  const indexOfKey = useCallback(
    (key: unknown): number => images.findIndex((image) => galleryItemKey(image) === key),
    [images]
  );

  const willReorder = useCallback(
    ({ active, over }: ReorderDrop): boolean => {
      const fromIndex = indexOfKey(active.id);
      const toIndex = indexOfKey(over?.id);

      return !isReordering && fromIndex !== toIndex && isSortable(fromIndex) && isSortable(toIndex);
    },
    [indexOfKey, isSortable, isReordering]
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent): void => {
      if (!willReorder(event)) {
        return;
      }

      onReorder?.(indexOfKey(event.active.id), indexOfKey(event.over?.id));
    },
    [willReorder, indexOfKey, onReorder]
  );

  return { sensors, sortableKeys, isSortable, indexOfKey, willReorder, onDragEnd };
};

export default useGridReorder;
